import i18n, { type AppLanguage, DEFAULT_LANGUAGE } from '@repo/i18n'
import {
	endOfDay,
	formatDistanceToNow,
	isToday,
	isYesterday,
	startOfDay,
	subDays
} from 'date-fns'
import { enUS, it, type Locale } from 'date-fns/locale'
import { format, formatInTimeZone } from 'date-fns-tz'
import type { DateRange } from 'react-day-picker'
import { z } from 'zod'

const locales = {
	en: enUS,
	it
} as const satisfies Record<AppLanguage['code'], Locale>

export const zDateSearchParam = z
	.union([
		z.date(),
		// A date-time without offset parses as local time, matching the
		// calendar-day intent of a plain date param.
		z.iso.date().transform((value) => new Date(`${value}T00:00:00`)),
		z.iso
			.datetime({ local: true, offset: true })
			.transform((value) => new Date(value))
	])
	.optional()
	.catch(undefined)

/**
 * The date-fns locale for `language`, or for the language in use when none is
 * named.
 *
 * **Name the language from anything that renders.** Reading it from the
 * singleton is correct at the instant of the call and wrong immediately after,
 * because the React Compiler memoizes a call on the arguments it can see and
 * `i18n.language` is not one of them: the cached string then survives a
 * language change, and the reader is shown a date in the language they just
 * left (OPSSTD-5458). `useLocaleFormat` in `#hooks/use-locale-format` is that
 * argument supplied reactively, and is what a component should call.
 *
 * The parameterless form stays for callers outside a render — event handlers,
 * mutation `onError` — where nothing is memoized and the singleton is the
 * current language by definition.
 */
const localeFor = (language?: string) => {
	const currentLang = language || i18n.language || DEFAULT_LANGUAGE.code
	return (
		locales[currentLang as keyof typeof locales] ||
		locales[DEFAULT_LANGUAGE.code]
	)
}

export const formatLocaleDate = (
	date: string | Date,
	formatType = 'P',
	language?: string
) => {
	return format(new Date(date), formatType, { locale: localeFor(language) })
}

export const getTimezone = () => {
	return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export const formatInTimezone = (
	date: string | Date,
	formatType = 'P',
	language?: string
) => {
	const timezone = getTimezone()
	return formatInTimeZone(new Date(date), timezone, formatType, {
		locale: localeFor(language)
	})
}

/**
 * How long ago something happened, in words.
 *
 * A date leaves the arithmetic to the reader: "sent on 19/08" is a subtraction
 * somebody has to perform to learn the only thing they wanted to know, and they
 * perform it wrong on the first of the month. "Pending for 7 days" is the
 * reading itself, which is what makes an invitation nobody has answered visible
 * as a problem rather than as a row of data.
 *
 * `addSuffix` turns the bare span into a direction — "7 days ago", "in 5 days" —
 * and is what a deadline needs, because a span alone cannot say which side of
 * now it falls on. Leave it off when the surrounding sentence already supplies
 * the direction, so the phrase does not say it twice.
 *
 * Read against the clock at the moment it is called, and nothing re-renders
 * this on a timer — so the phrase is as old as the render that produced it.
 * That is deliberate for a span measured in days. It is *not* a promise that
 * the value is recomputed on every render: under the React Compiler a call in
 * a component is memoized on `date` alone, which is why `language` is named
 * here rather than read from the singleton.
 */
export function formatDistanceToNowLocale(
	date: string | Date,
	options: { addSuffix?: boolean; language?: string } = {}
) {
	return formatDistanceToNow(new Date(date), {
		addSuffix: options.addSuffix ?? false,
		locale: localeFor(options.language)
	})
}

export function formatRecentDate(
	date: string | Date,
	options: {
		yesterday: string
		todayFormat?: string
		weekFormat?: string
		fallbackFormat?: string
		language?: string
	}
) {
	const value = new Date(date)

	if (isToday(value)) {
		return formatLocaleDate(
			value,
			options.todayFormat ?? 'p',
			options.language
		)
	}

	if (isYesterday(value)) {
		return options.yesterday
	}

	if (value > subDays(new Date(), 7)) {
		return formatLocaleDate(
			value,
			options.weekFormat ?? 'EEEE',
			options.language
		)
	}

	return formatLocaleDate(
		value,
		options.fallbackFormat ?? 'P',
		options.language
	)
}

export function dateToStartOfDayIso(date: Date | undefined) {
	return date ? startOfDay(date).toISOString() : undefined
}

export function dateToEndOfDayIso(date: Date | undefined) {
	return date ? endOfDay(date).toISOString() : undefined
}

export function toDateRange(
	from: Date | undefined,
	to: Date | undefined
): DateRange | undefined {
	return from || to ? { from, to } : undefined
}

export function getLocalCalendarDateKey(date: string | Date) {
	const value = new Date(date)
	return (
		value.getFullYear() * 10_000 + value.getMonth() * 100 + value.getDate()
	)
}

/**
 * A number of seconds in the largest whole unit it divides into: 900 seconds
 * is a quarter of an hour, and «15 minuti» is what somebody watching a clock
 * needs. Anything that does not divide evenly stays in seconds rather than
 * being rounded: these are security windows, and a rounded one read as exact
 * is worse than an awkward one read as exact.
 *
 * The arithmetic only, in one place (OPSSTD-5844). The sentence is each app's
 * own, with its own keys: one speaks to a platform operator, the others to
 * whoever shares a folder or manages people.
 */
export function inLargestWholeUnit(seconds: number) {
	if (seconds >= SECONDS_PER_HOUR && seconds % SECONDS_PER_HOUR === 0) {
		return { count: seconds / SECONDS_PER_HOUR, unit: 'hours' } as const
	}
	if (seconds >= SECONDS_PER_MINUTE && seconds % SECONDS_PER_MINUTE === 0) {
		return { count: seconds / SECONDS_PER_MINUTE, unit: 'minutes' } as const
	}
	return { count: seconds, unit: 'seconds' } as const
}

const SECONDS_PER_MINUTE = 60
const SECONDS_PER_HOUR = 3600
